import React, { useState } from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { useEffect } from "react";
import GOOD_CHILLING_VIBE from "../../audio/good_chilling_vibe.mp3";
import { MDBBtn } from "mdbreact";

// Initialize Echo only if environment variables are available
let echoInstance = null;

const initializeEcho = () => {
  // Skip initialization in test environment or if already initialized
  if (process.env.NODE_ENV === 'test' || echoInstance || !process.env.REACT_APP_REVERB_APP_KEY) {
    return;
  }

  try {
    const token = JSON.parse(localStorage.getItem("accessToken"));
    const base_url = process.env.REACT_APP_API_URL;

    window.Pusher = Pusher;
    echoInstance = new Echo({
      broadcaster: "reverb",
      key: process.env.REACT_APP_REVERB_APP_KEY,
      wsHost: process.env.REACT_APP_REVERB_HOST,
      wsPort: process.env.REACT_APP_REVERB_PORT,
      wssPort: process.env.REACT_APP_REVERB_PORT,
      forceTLS: (process.env.REACT_APP_REVERB_SCHEME ?? "https") === "https",
      enabledTransports: ["ws", "wss"],
      authEndpoint: `${base_url}broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    
    window.Echo = echoInstance;
  } catch (error) {
    console.warn("Failed to initialize Echo:", error);
  }
};

function PublishSubscriber() {
  const [showOrder, setShowOrder] = useState({ state: false, order: {} });
  const [audio] = useState(() => new Audio(GOOD_CHILLING_VIBE));
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  // Get user data safely
  const getUserData = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const teamId = JSON.parse(localStorage.getItem("teamId"));
      const company_id = user?.data?.groups[teamId]?.company_id;
      
      const isCourier = user?.data?.team_roles[teamId]?.roles?.some((role) =>
        role.includes("company_courier")
      );
      const isEmployee = user?.data?.team_roles[teamId]?.roles?.some((role) =>
        role.includes("company_employee")
      );

      return { user, teamId, company_id, isCourier, isEmployee };
    } catch (error) {
      console.warn("Error getting user data:", error);
      return { user: null, teamId: null, company_id: null, isCourier: false, isEmployee: false };
    }
  };

  const { company_id, isCourier, isEmployee } = getUserData();

  // Audio unlock effect
  useEffect(() => {
    if (audioUnlocked) return;

    const unlockAudio = () => {
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          setAudioUnlocked(true);
        })
        .catch(() => {});
      document.removeEventListener("click", unlockAudio);
    };

    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, [audio, audioUnlocked]);

  // WebSocket subscription effect
  useEffect(() => {
    // Skip if no company_id or in test environment
    if (!company_id || process.env.NODE_ENV === 'test') {
      return;
    }

    // Initialize Echo if not already done
    initializeEcho();

    // Skip if Echo failed to initialize
    if (!window.Echo) {
      console.warn("Echo not available, skipping WebSocket subscription");
      return;
    }

    console.log("Subscribing to channel...");

    const channel = window.Echo.private(`company.${company_id}`).listen(
      "BroadcastCreatedCourierOrder",
      (event) => {
        setShowOrder({
          state: true,
          order: event?.courier_order || {},
        });
        
        // Try to play audio, with fallback to browser notification
        audio.play().catch((err) => {
          console.warn("Audio blocked by browser:", err);
          
          // Fallback to browser notification
          if (Notification.permission === 'granted') {
            new Notification('New Order!', {
              body: 'You have a new courier order',
              icon: '/favicon.png'
            });
          } else if (Notification.permission === 'default') {
            Notification.requestPermission();
          }
        });
      }
    );

    return () => {
      console.log("Unsubscribing from channel...");
      try {
        channel.stopListening("BroadcastCreatedCourierOrder");
      } catch (error) {
        console.warn("Error unsubscribing:", error);
      }
    };
  }, [company_id, audio]);

  if (showOrder.state && (isCourier || isEmployee)) {
    return (
      <div className="order-overlay">
        <div className="call-wrapper">
          <span className="ring" />
          <p className="text-lg font-bold">You have a new order!</p>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <MDBBtn
              color="secondary"
              type="button"
              onClick={() => {
                setShowOrder({ state: false, order: {} });
                audio.pause();
                audio.currentTime = 0;
              }}
            >
              Close
            </MDBBtn>
            <MDBBtn color="primary" type="button">
              {isCourier ? (
                <a
                  href="/courier/orders"
                  className="text-decoration-none text-white"
                >
                  Orders
                </a>
              ) : (
                <a href="/orders" className="text-decoration-none text-white">
                  Orders
                </a>
              )}
            </MDBBtn>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default PublishSubscriber;
